import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { WarningSeverityIcon, type MobileCardRendererProps, type RenderCellOptions } from '@talos/kyoko';
import type { ReactNode } from 'react';
import { MobileBlotterCard } from '../MobileBlotterCardLayout';
import { useCompactMobileCardLayout } from '../useCompactMobileCardLayout';
import { getRequiredPositionMetrics, POSITION_EQUIVALENT_COL_ID } from './mobilePositionMetric';
import { getPositionWarning, type PositionWarningSource } from './mobilePositionWarnings';

export const MOBILE_POSITION_CARD_HEIGHT = 108;
export const COMPACT_MOBILE_POSITION_CARD_HEIGHT = 52;

export function useMobilePositionCardLayout() {
  return useCompactMobileCardLayout(MOBILE_POSITION_CARD_HEIGHT, COMPACT_MOBILE_POSITION_CARD_HEIGHT);
}

const VISIBLE_COLUMN_RENDER_OPTIONS = { overrideColumnVisibility: true } satisfies RenderCellOptions;

/**
 * Mobile card for a single Position leaf row. Designed to be passed to
 * `MobileDataTable` via the `cardRenderer` prop. Re-uses the desktop column
 * renderers via `renderCell` so currency formatting, sentiment colouring and
 * increment handling stay consistent.
 *
 * Generic over the row type so it works for both `Position` and
 * `UnifiedPosition`. Instrument column id varies by blotter (`instrument` on
 * derivatives, `Asset` on spot / unified / sub-accounts).
 *
 * Compact layout is a single row: symbol and amount in home currency.
 */
export function MobilePositionCard<TRow extends PositionWarningSource>({
  data,
  renderCell,
  onPress,
  compact = false,
}: MobileCardRendererProps<TRow>) {
  const { homeCurrency } = useDisplaySettings();
  const marketAccount = renderCell('MarketAccount');
  const showReconWarningColumn = renderCell('reconWarning') != null;
  const reconWarning = showReconWarningColumn ? getPositionWarning(data) : undefined;
  const [positionMetric, ...secondaryMetrics] = getRequiredPositionMetrics(renderCell, homeCurrency);
  const compactMetric = secondaryMetrics.find(metric => metric.colId === POSITION_EQUIVALENT_COL_ID) ?? positionMetric;

  return (
    <MobileBlotterCard
      compact={compact}
      onPress={onPress}
      testId="mobile-position-card"
      instrumentTestId="mobile-position-card-asset"
      marketAccountTestId="mobile-position-card-market-account"
      warningTestId="mobile-position-card-recon-warning"
      instrument={renderInstrumentCell(renderCell)}
      marketAccount={marketAccount}
      warning={
        reconWarning ? <WarningSeverityIcon severity={reconWarning.severity} data-testid="warning-bar" /> : undefined
      }
      primaryMetric={positionMetric}
      compactMetric={compactMetric}
      secondaryMetrics={secondaryMetrics}
    />
  );
}

function renderInstrumentCell(renderCell: MobileCardRendererProps<unknown>['renderCell']): ReactNode {
  return renderCell('instrument', VISIBLE_COLUMN_RENDER_OPTIONS) ?? renderCell('Asset', VISIBLE_COLUMN_RENDER_OPTIONS);
}
